import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import GetIcon from "@/components/font-awesome-icon";
import { faXmark, faCheck } from "@fortawesome/free-solid-svg-icons";
import UIButton from "@/components/ui-controls/button";
import useCRMTheme from "@/hooks/useCRMTheme";

// ─── Plan Data ────────────────────────────────────────────────────────────────
const PLANS = [
  {
    name: "Basic",
    subtitleParts: null,
    features: [
      { label: "Appointment Types:", value: "03", included: true },
      { label: "Members:", value: "01", included: true },
      { label: "One-Off Meetings:", value: "5/month", included: true },
      { label: "Leads/Contacts:", value: "100", included: true },
      { label: "Custom Fields:", value: "upto 5", included: true },
      { label: "Reminders (SMS/Email)", value: null, included: false },
      { label: "Routing", value: null, included: false },
      { label: "Google Integration", value: null, included: false },
      { label: "Zoom Integration", value: null, included: false },
    ],
  },
  {
    name: "Pro",
    subtitleParts: [{ text: "Basic", underline: true }, { text: " + advanced" }],
    features: [
      { label: "Appointment Types:", value: "10", included: true },
      { label: "Members:", value: "05", included: true },
      { label: "One-Off Meetings:", value: "25/month", included: true },
      { label: "Leads/Contacts:", value: "1000", included: true },
      { label: "Custom Fields:", value: "upto 10", included: true },
      { label: "Reminders (SMS/Email):", value: "upto 02", included: true },
      { label: "Routing", value: null, included: true },
      { label: "Google Integration", value: null, included: true },
      { label: "Zoom Integration", value: null, included: true },
    ],
  },
  {
    name: "Pro+",
    subtitleParts: [{ text: "Pro", underline: true }, { text: " + enhanced scalability" }],
    features: [
      { label: "Appointment Types:", value: "Unlimited", included: true },
      { label: "Members:", value: "Unlimited", included: true },
      { label: "One-Off Meetings:", value: "Unlimited", included: true },
      { label: "Leads/Contacts:", value: "Unlimited", included: true },
      { label: "Custom Fields:", value: "Unlimited", included: true },
      { label: "Reminders (SMS/Email):", value: "Unlimited", included: true },
      { label: "Routing", value: null, included: true },
      { label: "Google Integration", value: null, included: true },
      { label: "Zoom Integration", value: null, included: true },
    ],
  },
];

// ─── Plan Card ────────────────────────────────────────────────────────────────
const PlanCard = ({ plan, theme }) => {
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: theme.container,
        border: `1px solid ${theme.stroke}`,
        borderRadius: "12px",
        boxShadow: "0px 2px 12px 0px rgba(20, 20, 43, 0.08)",
        display: "flex",
        flexDirection: "column",
        p: "16px",
        gap: "16px",
        position: "relative",
        minWidth: 0,
      }}
    >
      {/* Plan Name */}
      <Typography
        sx={{
          fontSize: "1.75rem",
          fontWeight: 600,
          color: theme.heading,
          lineHeight: "normal",
          fontFamily: "Faktum, sans-serif",
        }}
      >
        {plan.name}
      </Typography>

      {/* Subtitle */}
      {plan.subtitleParts ? (
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: theme.grey_text,
            fontFamily: "Faktum, sans-serif",
            lineHeight: "normal",
            mt: "-8px",
          }}
        >
          {plan.subtitleParts.map((part, i) =>
            part.underline ? (
              <Box
                key={i}
                component="span"
                sx={{ textDecoration: "underline", fontWeight: 500 }}
              >
                {part.text}
              </Box>
            ) : (
              <span key={i}>{part.text}</span>
            )
          )}
        </Typography>
      ) : (
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: theme.grey_text,
            fontFamily: "Faktum, sans-serif",
            lineHeight: "normal",
            mt: "-8px",
          }}
        >
          Core features to get started
        </Typography>
      )}

      {/* Divider */}
      <Box sx={{ height: "1px", backgroundColor: theme.stroke, mx: "-24px" }} />

      {/* What's included */}
      <Typography
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 700,
          color: theme.sub_heading,
          fontFamily: "Faktum, sans-serif",
        }}
      >
        What&apos;s included
      </Typography>

      {/* Feature List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
        {plan.features.map((feature, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              sx={{
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                backgroundColor: feature.included
                  ? "var(--primary-20, #e8f0fc)"
                  : "var(--base-40, #F0F0F1)",
              }}
            >
              <GetIcon
                icon={feature.included ? faCheck : faXmark}
                iconStyles={{
                  fontSize: "9px",
                  marginRight: 0,
                  color: feature.included
                    ? theme.primary_button
                    : theme.grey_text,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: theme.text,
                fontFamily: "Faktum, sans-serif",
                lineHeight: "normal",
              }}
            >
              {feature.label}{" "}
              {feature.value && (
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {feature.value}
                </Box>
              )}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* CTA Button */}
      <UIButton
        title="Contact Sales"
        onClick={() => {}}
        sx={{
          mt: "8px",
          width: "100%",
          height: "44px",
          backgroundColor: theme.primary_button,
          "&:hover": { backgroundColor: theme.hover_color },
          borderRadius: "8px",
          fontSize: "0.9375rem",
          fontWeight: 500,
          fontFamily: "Faktum, sans-serif",
          textTransform: "none",
          color: theme.primary_button_text,
        }}
      />
    </Box>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const PackagesModal = ({ open, onClose }) => {
  const { selectedTheme } = useCRMTheme();
  const theme = selectedTheme?.custom || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "8px",
          overflow: "hidden",
          maxWidth: "900px",
          width: "100%",
          backgroundColor: theme.background,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          backgroundColor: theme.header_bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "24px",
          py: "14px",
          minHeight: "auto",
        }}
      >
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            color: theme.header_text,
            fontFamily: "Faktum, sans-serif",
          }}
        >
          Select a Plan
        </Typography>
        <Box
          onClick={onClose}
          sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
        >
          <GetIcon icon={faXmark} iconStyles={{ fontSize: "1.25rem", color: theme.header_text }} />
        </Box>
      </DialogTitle>

      {/* Body */}
      <DialogContent
        sx={{
          backgroundColor: theme.background,
          px: "24px",
          pt: "24px !important",
          pb: "24px",
        }}
      >
        {/* Title */}
        <Typography
          sx={{
            textAlign: "center",
            mb: "24px",
            fontSize: "1.5rem",
            fontWeight: 700,
            color: theme.sub_heading,
            fontFamily: "Faktum, sans-serif",
            lineHeight: "normal",
          }}
        >
          Flexible{" "}
          <Box
            component="span"
            sx={{
              background:
                `linear-gradient(-87deg, #8b3f6f 46%, var(--primary-1, #311798) 64%, var(--palette-orange_shade, #fa713c) 92%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Plans
          </Box>{" "}
          for Every Stage of Growth
        </Typography>

        {/* Cards Row */}
        <Box sx={{ display: "flex", gap: "16px", alignItems: "stretch" }}>
          {PLANS.map((plan) => (
            <PlanCard key={plan.name} plan={plan} theme={theme} />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PackagesModal;
